/**
 * MapDropdown - Selector de tipo de mapa
 * 
 * Dropdown para elegir: Google Maps o Mapa PTEL.
 * Actualiza iconos de los botones pin en la tabla.
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 1.0.0
 */

import React, { useState, useRef, useEffect, type FC, type CSSProperties } from 'react';
import { colors, typography, borderRadius, shadows, transitions } from '@/design-system/v2';

// ============================================================================
// TYPES
// ============================================================================

export type MapType = 'google' | 'ptel' | 'dera';

export interface MapDropdownProps {
  /** Tipo de mapa seleccionado */
  value?: MapType;
  /** Callback al cambiar selección */
  onChange?: (type: MapType) => void;
}

// ============================================================================
// MAP CONFIG
// ============================================================================

const MAP_CONFIG: Record<MapType, { label: string; icon: React.ReactNode }> = {
  google: {
    label: 'Abrir en Google Maps',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  ptel: {
    label: 'Abrir en Mapa PTEL',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" />
        <line x1="9" y1="3" x2="9" y2="18" />
        <line x1="15" y1="6" x2="15" y2="21" />
      </svg>
    ),
  },
  // Visor DERA (IECA) — URL vía generateLocationURLs().deraViewer (LocationURLGenerator.ts),
  // que consume UTM30 EPSG:25830 nativo del visor. El consumidor cablea la apertura.
  dera: {
    label: 'Abrir en visor DERA',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
  },
};

// ============================================================================
// STYLES
// ============================================================================

const STYLES = {
  container: {
    position: 'relative' as const,
  } as CSSProperties,

  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.md,
    color: colors.fg.secondary,
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.base,
    cursor: 'pointer',
    transition: transitions.base,
  } as CSSProperties,

  buttonHover: {
    borderColor: colors.primary,
    color: colors.primary,
  } as CSSProperties,

  icon: {
    width: 14,
    height: 14,
  } as CSSProperties,

  chevron: {
    width: 10,
    height: 10,
  } as CSSProperties,

  menu: {
    position: 'absolute' as const,
    top: 'calc(100% + 4px)',
    right: 0,
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.lg,
    minWidth: 180,
    padding: '4px',
    zIndex: 50,
    boxShadow: shadows.lg,
  } as CSSProperties,

  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    color: colors.fg.secondary,
    fontSize: typography.fontSize.base,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    transition: transitions.base,
    background: 'transparent',
    border: 'none',
    width: '100%',
    fontFamily: typography.fontFamily.sans,
    textAlign: 'left' as const,
  } as CSSProperties,

  menuItemHover: {
    background: colors.bg.tertiary,
    color: colors.fg.primary,
  } as CSSProperties,

  menuItemActive: {
    color: colors.primary,
  } as CSSProperties,
};

// ============================================================================
// ICONS
// ============================================================================

const ChevronDown: FC = () => (
  <svg
    viewBox="0 0 24 24"
    style={STYLES.chevron}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

export const MapDropdown: FC<MapDropdownProps> = ({
  value = 'google',
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<MapType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSelect = (type: MapType) => {
    onChange?.(type);
    setIsOpen(false);
  };

  const currentConfig = MAP_CONFIG[value];

  const buttonStyle: CSSProperties = {
    ...STYLES.button,
    ...(buttonHovered ? STYLES.buttonHover : {}),
  };

  const getItemStyle = (type: MapType): CSSProperties => ({
    ...STYLES.menuItem,
    ...(hoveredItem === type ? STYLES.menuItemHover : {}),
    ...(value === type ? STYLES.menuItemActive : {}),
  });

  return (
    <div style={STYLES.container} ref={containerRef}>
      <button
        style={buttonStyle}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setButtonHovered(true)}
        onMouseLeave={() => setButtonHovered(false)}
      >
        <span style={STYLES.icon}>{currentConfig.icon}</span>
        <span>{currentConfig.label}</span>
        <ChevronDown />
      </button>

      {isOpen && (
        <div style={STYLES.menu}>
          {(Object.keys(MAP_CONFIG) as MapType[]).map((type) => (
            <button
              key={type}
              style={getItemStyle(type)}
              onClick={() => handleSelect(type)}
              onMouseEnter={() => setHoveredItem(type)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span style={STYLES.icon}>{MAP_CONFIG[type].icon}</span>
              {MAP_CONFIG[type].label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapDropdown;
